import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { LinearProgress } from 'src/componentes/LinearProgress';
import { UncontrolledTooltip, Spinner } from 'reactstrap';
import ReactMarkdown from 'react-markdown';

import Dialog from 'src/componentes/dialog';
import Input from 'src/componentes/input';
import LucideIcon from 'src/componentes/lucide-icon';
import Avatar from 'src/componentes/avatar';
import Confirm from 'src/componentes/NewConfirm';
import ContentManager from 'src/componentes/content-manager';
import { capitalizeFirstLetter } from 'src/utils/string';
import css from 'src/utils/css';
import { SophieIcon } from 'src/componentes/icons';
import useChatIA from 'src/hooks/UseChatIA';

const classes = css`
  .dialogIA {
    z-index: 999;
  }
`;

let observer = null;

const AssistenteVirtual = ({
  user,
  itemId,
  prompt,
  title,
  actionsToolBar = [],
  actions,
  customContent,
  handleEditButton,
  closeCallEdit = false,
  sendBack = false,
  interactionType,
  dontShowFeedbackSophie = false,
  usePromptButtons = false,
  listButtons = [],
  customGreetins = null,
  ...rest
}) => {
  const ref = useRef();
  const customContentRef = useRef(null);
  const intl = useIntl();

  const [customObject, setCustomObject] = useState({});
  const [openCustomContent, setOpenCustomContent] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    messages,
    historyContext,
    isReady,
    isStreaming,
    promptButtonsVisible,
    newMessage,
    setNewMessage,
    sendMessage,
    handlePromptButtonClick,
    copiedMessageIndex,
    copyMessage,
    requestClose,
  } = useChatIA({
    user,
    prompt,
    interactionType,
    usePromptButtons,
    listButtons,
    dontShowFeedbackSophie,
  });

  const handleScrollBottom = () => {
    const element = document.getElementsByClassName('chat-ia-container')[0];
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (isReady) handleScrollBottom();
  }, [messages, isReady]);

  useEffect(() => {
    if (openCustomContent && customContentRef.current) {
      customContentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [openCustomContent]);

  const handleAddObserver = () => {
    const body = document.body;
    observer = new MutationObserver(() => {
      const isDrawerOpen = document.body.classList.contains('drawer-open');
      setIsDrawerOpen(isDrawerOpen);
    });

    observer.observe(body, {
      attributes: true,
    });
  };

  const handleRemoveObserver = () => {
    observer && observer.disconnect();
    observer = null;
  };

  useEffect(() => {
    handleAddObserver();

    return () => {
      handleRemoveObserver();
    };
  }, []);

  const handleSetCustomObject = (field, value) => {
    setCustomObject((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClose = () => {
    if (ref.current) ref.current.close();
    requestClose();
  };

  const handleEditButtonCustom = () => {
    handleClose();
    handleEditButton &&
      handleEditButton({
        listHistoryContext: historyContext,
        itemId: itemId,
      });
  };

  const confirmClose = () => {
    ContentManager.addContent(
      <Confirm
        cancelColor="primary"
        showCancel={true}
        labelConfirm={intl.formatMessage({ id: 'label.fechar' })}
        primaryMessage={intl.formatMessage({
          id: 'voceEstaFechandoModalSophie',
        })}
        secondaryMessage={intl.formatMessage({
          id: 'historicoSophieSeraPerdido',
        })}
        emphasysMessage={intl.formatMessage({
          id: 'desejaFechaloMesmoAssim',
        })}
        onSaved={handleClose}
      />
    );
  };

  const renderQuestion = (message, index) => {
    const bgClass = message.isUser ? 'bg-white' : 'bg-primary-light';
    const splitMessage = message.text?.split('\n');

    return (
      <div key={`Q-${index}`} className="d-flex py-3 px-6">
        <div style={{ flex: 1, display: 'flex' }}>
          <div
            className={`${bgClass} rounded-3 p-3 d-flex`}
            style={{
              width: '100%',
              alignItems: 'flex-start',
              borderRadius: 16,
            }}
          >
            {message.isUser ? (
              <Avatar
                style={{ marginRight: 12 }}
                size={32}
                label={user?.nome}
                foto={user?.fotoArquivo}
                showLabel={false}
              />
            ) : (
              <div
                className="d-flex justify-content-center align-items-center bg-white rounded-circle p-1 border"
                style={{
                  marginRight: 12,
                  minHeight: 30,
                  height: 30,
                  minWidth: 30,
                  width: 30,
                }}
              >
                <SophieIcon size={18} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              {splitMessage?.map((text, idx) => (
                <div
                  key={`M-${idx}`}
                  style={{ marginBottom: idx > 0 ? 10 : 0 }}
                >
                  <ReactMarkdown>{text}</ReactMarkdown>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginTop: 12,
                }}
              >
                <div
                  id={`tooltip-copy-${index}`}
                  style={{ display: 'inline-block' }}
                >
                  <LucideIcon
                    size={16}
                    className="text-primary cursor-pointer"
                    icon={
                      copiedMessageIndex === index ? 'check' : 'content-copy'
                    }
                    onClick={() => copyMessage(message.text, index)}
                  />
                </div>
                <UncontrolledTooltip
                  target={`tooltip-copy-${index}`}
                  placement="bottom"
                >
                  {copiedMessageIndex === index
                    ? intl.formatMessage({ id: 'label.copiado' })
                    : intl.formatMessage({ id: 'label.copiar' })}
                </UncontrolledTooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13 && !event.shiftKey) {
      sendMessage();
    }
  };

  return (
    <Dialog
      className={sendBack ? classes.dialogIA : ''}
      width={900}
      height={'100%'}
      onRequestClose={confirmClose}
      mouseDownClose={true}
      respeitarAltura
      ref={ref}
      {...rest}
    >
      <div className="d-flex align-items-center px-6" style={{ maxWidth: 800 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SophieIcon size={20} style={{ marginRight: 8 }} />
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#222',
            }}
          >
            {intl.formatMessage({ id: 'labelActioIA' })}
          </span>
        </div>
        <div
          className="ml-3"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 700,
            minWidth: 0,
            flex: 1,
            fontSize: '0.95rem',
          }}
          title={title}
        >
          {title}
        </div>
      </div>
      <div
        className="d-flex flex-column"
        style={{
          height: promptButtonsVisible
            ? 'calc(100% - 256px)'
            : 'calc(100% - 220px)',
        }}
      >
        {!messages?.some((msg) => msg.text !== '') ? (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center px-7">
            {promptButtonsVisible ? (
              <div style={{ width: '100%', textAlign: 'center' }}>
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: '#BAD5DC',
                      opacity: 0.2,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 50,
                      height: 50,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SophieIcon size={22} />
                  </div>
                </div>
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                >
                  {intl.formatMessage({ id: 'labelActioIA' })}
                </div>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    color: '#888',
                    marginBottom: 8,
                  }}
                >
                  {capitalizeFirstLetter(
                    intl.formatMessage({ id: 'labelPorActio' })
                  )}
                </div>
                <div
                  style={{
                    fontSize: '0.95rem',
                    color: '#444',
                    marginBottom: 24,
                    maxWidth: 500,
                    margin: '0 auto',
                  }}
                >
                  {customGreetins}
                </div>
              </div>
            ) : (
              <LinearProgress style={{ width: '50%' }} />
            )}
          </div>
        ) : (
          <div
            className="new-list-tree overflow-auto chat-ia-container"
            style={{
              flexGrow: 1,
              overflowX: 'hidden',
              width: '100%',
            }}
          >
            {messages?.map((msg, index) =>
              msg.isVisible ? renderQuestion(msg, index) : ''
            )}
          </div>
        )}
      </div>
      {promptButtonsVisible && (
        <div className="px-6" style={{ marginBottom: 0 }}>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {listButtons.map((btn, idx) => (
              <div
                key={idx}
                style={{
                  flex: '1 1 calc(33.333% - 8px)',
                  minWidth: 0,
                  maxWidth: '33.333%',
                  marginBottom: 8,
                  display: 'flex',
                }}
              >
                <button
                  type="button"
                  className="btn"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '8px 8px',
                    fontSize: '0.78rem',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                    color: '#6c757d',
                    border: '1px solid #eee',
                    borderRadius: 6,
                    boxShadow: 'none',
                  }}
                  onClick={() =>
                    handlePromptButtonClick(btn.prompt, btn.nameButton)
                  }
                >
                  {btn.nameButton}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div
        className="py-1 px-6"
        style={{
          background: '#fff',
          marginTop: 0,
        }}
      >
        <div className="d-flex align-items-center">
          <div
            className="w-100 mr-4"
            onKeyDown={(event) => handleKeyDown(event)}
          >
            <Input
              disabled={
                (!isReady || isStreaming || isDrawerOpen) &&
                !promptButtonsVisible
              }
              formStyle={{ width: '100%' }}
              style={{ padding: 25 }}
              type="textarea"
              rows={2}
              maxLength={1000}
              placeholder={intl.formatMessage({
                id: 'digiteNovasInstrucoesAqui',
              })}
              model={{
                value: newMessage,
                requestChange: setNewMessage,
              }}
            />
          </div>
          {(isReady || promptButtonsVisible) &&
            (!isStreaming || promptButtonsVisible ? (
              <LucideIcon
                onClick={sendMessage}
                style={{ marginTop: -14 }}
                size={24}
                className="text-primary cursor-pointer"
                icon="send"
              />
            ) : (
              <Spinner style={{ marginTop: -15 }} color="primary" size="sm" />
            ))}
        </div>
        <div className="d-flex align-items-center justify-content-center mb-2 mt-n2">
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
            {intl.formatMessage({ id: 'avisoSophie' })}
          </span>
        </div>
        <div className="pull-right">
          {actionsToolBar &&
            !closeCallEdit &&
            actionsToolBar.map((a, index) =>
              React.cloneElement(a, {
                key: index + 4,
              })
            )}
          {actionsToolBar &&
            closeCallEdit &&
            actionsToolBar.map((a, index) =>
              React.cloneElement(a, {
                key: index + 4,
                onClick: handleEditButtonCustom,
              })
            )}
        </div>
        {actions && actions(openCustomContent, setOpenCustomContent)}
        {openCustomContent && customContent && (
          <div style={{ paddingBottom: 16 }} ref={customContentRef}>
            {customContent(customObject, handleSetCustomObject)}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default AssistenteVirtual;
